import React from 'react';
import { DeviceFailureGroup } from '../utils/deviceSyncError';

/**
 * A device-sync failure, as a toast worth reading.
 *
 * It used to be one string — `"<devices>: <cause>"` — handed to `toast.error`. At the width of a
 * toast that wraps into a paragraph, and the order is backwards: the operator reads two lines of
 * hardware names and only reaches the actual failure on line four. Real example from the field:
 *
 *     Base Presidencial - Saída Garagem,
 *     Oficina - Frota Panther:
 *     Comunicação direta falhou —
 *     dispositivo inacessível
 *
 * Four ragged lines, no hierarchy, and the one thing that matters last. So the two parts separate:
 * the CAUSE leads in the toast's own weight, and the devices follow as a quieter second line —
 * what happened, then where. The device line is clamped to two lines, because a sync across a
 * dozen readers must not turn a toast into a wall of text.
 */
export function DeviceFailureToast({ group }: { group: DeviceFailureGroup }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {/* A LABEL, not a sentence: at title weight the full cause wrapped onto two bold lines
          and turned a notification into a banner. The sentence survives below it. */}
      <span className="text-sm font-semibold leading-snug truncate" title={group.message}>
        {group.title}
      </span>
      <span className="text-xs opacity-70 leading-snug break-words line-clamp-2">
        {group.message}
      </span>
      <span
        className="text-xs opacity-70 leading-snug break-words line-clamp-2"
        title={group.deviceNames.join(', ')}
      >
        {group.deviceList}
      </span>
      {/* The code is what gets quoted to support: the message is prose, it is translated, and
          some paths prefix it — none of that survives a phone call. Its own line, below the
          ones the operator actually reads, so it informs without competing. */}
      <span className="text-[10px] font-mono opacity-50 leading-snug break-words tracking-wide">
        {group.code}
      </span>
    </div>
  );
}

/**
 * The element to hand to `toast.error`.
 *
 * A function rather than JSX at each call site so the three places that report sync failures
 * (PersonDetailModal, PersonProfile, VisitorRegistrationModal) cannot drift into three different
 * toasts for the same event.
 */
export function deviceFailureToast(group: DeviceFailureGroup): React.ReactElement {
  return <DeviceFailureToast group={group} />;
}
